/**
 * O ícone que age na tela pergunta o que a ação exige — issue #408, segunda onda.
 *
 * É o `ActionButton` para quem não tem texto: remover o laudo de um exame, cancelar um evento
 * do eSocial, reenviar um convite. Em ícone o `tooltip` **é** o nome acessível, e por isso ele
 * nunca vira só o motivo do bloqueio — vem o rótulo e o motivo, e o `aria-label` continua
 * dizendo qual ação era.
 *
 * O `<span>` está sempre presente, e não só quando bloqueia: botão desabilitado não emite
 * evento de mouse, e sem o invólucro o tooltip do estado bloqueado nunca abriria.
 */

import React from 'react';
import { IconButton, Tooltip, type IconButtonProps } from '@mui/material';

import { useAcao } from '../../authorization/useVeredito';
import type { Modulo } from '../../types/manifesto';

export interface ActionIconButtonProps extends Omit<IconButtonProps, 'aria-label'> {
  /** A permissão que o servidor exige da ação. Uma lista significa "qualquer uma delas". */
  acao: string | readonly string[];
  /** Como a ação se chama — vai para o tooltip e para o nome acessível. */
  rotulo: string;
  /** O módulo comercial que cobre a ação, quando não é o da tela. */
  modulo?: Modulo;
}

export const ActionIconButton: React.FC<ActionIconButtonProps> = ({
  acao,
  rotulo,
  modulo,
  disabled,
  onClick,
  children,
  ...props
}) => {
  const controle = useAcao(acao, rotulo, { modulo });

  return (
    <Tooltip title={controle.tooltip}>
      <span>
        <IconButton
          {...props}
          aria-label={rotulo}
          disabled={controle.disabled || disabled}
          onClick={controle.disabled ? undefined : onClick}
        >
          {children}
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default ActionIconButton;
